<script lang="ts" context="module">
	declare global {
		interface Window {
			// eslint-disable-next-line @typescript-eslint/consistent-type-imports
			smartform: import("../../typings/smartform.js").ISmartform;
		}
	}
</script>

<script lang="ts">
	import { getGlobalSettings } from "../getGlobalSettings.js";
	import type { SmartFormInstance } from "../../typings/smartform.js";

	const { SMARTFORM } = getGlobalSettings();

	// It's just super hard to know when the smartform instance is ready, so we have to wait for it. Also the company service is not enabled immediately it seems.
	async function waitForSmartformInstance(): Promise<SmartFormInstance> {
		return new Promise((resolve) => {
			const interval = setInterval(() => {
				const instance = window.smartform.getInstance();
				if (instance && window.smartform.isCompanyServiceEnabled()) {
					resolve(instance);
					clearInterval(interval);
				}
			});
		});
	}

	let isInitialized = false;
	function initializeSmartform(): void {
		if (isInitialized) {
			return;
		}
		isInitialized = true;

		const smartformBody = document.querySelector("#smartform-body");

		function getSmartformInputs(): (HTMLInputElement | HTMLTextAreaElement)[] {
			return Array.from(
				smartformBody?.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>("input, textarea") ?? [],
			).filter((input) => input.className.includes("smartform-"));
		}

		let currentSmartformInputs = getSmartformInputs();

		function addValidationCallback(): void {
			window.smartform.unbindForm("smartform-instance");
			window.smartform.rebindAllForms(async () => {
				const instance = await waitForSmartformInstance();
				instance.companyControl.addValidationCallback(() => {
					setTimeout(() => {
						for (const input of currentSmartformInputs) {
							input.dispatchEvent(new Event("input", { bubbles: true }));
							input.dispatchEvent(new Event("change", { bubbles: true }));
						}
					}, 250);
				});
			});
		}

		addValidationCallback();

		// Listen for changes in the DOM, in case new smartform inputs are added
		const observer = new MutationObserver(() => {
			const newSmartformInputs = getSmartformInputs();
			const areInputsSame =
				currentSmartformInputs.length === newSmartformInputs.length &&
				currentSmartformInputs.every((input) => newSmartformInputs.includes(input));
			if (areInputsSame) {
				return;
			}
			currentSmartformInputs = newSmartformInputs;
			addValidationCallback();
		});

		if (smartformBody) {
			observer.observe(smartformBody, { childList: true, subtree: true });
		}
	}

	/* eslint-disable */
</script>

<svelte:head>
	{@html `<script type="text/javascript" src="https://client.smartform.cz/v2/smartform.js" async></script>`}
	{@html `
	<script type="text/javascript">
		var smartform = smartform || {};

		smartform.beforeInit = () => {
			smartform.setClientId('${SMARTFORM}');
			smartform.enableSetAutocomplete(true);
		};
	</script>
	`}
</svelte:head>

<!-- TODO smartform not inicialized, when directly clicking on form input -->
<svelte:window on:keydown={initializeSmartform} on:click={initializeSmartform} />

<div role="none" id="smartform-body" class="contents">
	<slot />
</div>
